export type ConciergeLinkAction =
  | { kind: 'internal'; href: string }
  | { kind: 'support'; conversationId?: string }
  | { kind: 'external'; href: string }
  | { kind: 'invalid' };

export interface ActionablePushAction {
  actionId: string;
  labelKey: string;
  deepLink?: string;
  params: Record<string, string>;
}

export interface ActionablePushPayload {
  type: string;
  category: string;
  deepLink: string;
  primaryAction: string;
  actions: ActionablePushAction[];
}

export function resolveConciergeLink(raw?: string | null): ConciergeLinkAction {
  const href = raw?.trim();
  if (!href) return { kind: 'invalid' };

  let url: URL;
  try {
    const base = typeof window === 'undefined' ? 'https://home.local' : window.location.origin;
    url = new URL(href, base);
  } catch {
    return { kind: 'invalid' };
  }

  if (url.protocol === 'lscloud:') return resolveLsCloudLink(url);

  if (url.protocol !== 'ls-app:') {
    return url.protocol === 'http:' || url.protocol === 'https:' ? { kind: 'external', href } : { kind: 'invalid' };
  }

  const parts = url.pathname.split('/').filter(Boolean).map(safeDecode);
  switch (url.hostname) {
    case 'device': {
      // 精確兩段 {id}/settings(凍結目錄形狀;與 iOS/Android parser 同步收斂)。
      const deviceId = parts[0];
      if (parts.length === 2 && deviceId && parts[1] === 'settings') {
        return { kind: 'internal', href: `/devices/${encodeURIComponent(deviceId)}` };
      }
      break;
    }
    case 'subscription':
      if (parts.length === 1 && parts[0] === 'manage') return { kind: 'internal', href: '/account' };
      break;
    case 'support':
      // 裸 ls-app://support 不在凍結目錄(foundation/24 §7 四組),不再放行。
      if (parts.length === 1 && parts[0] === 'contact') return { kind: 'support' };
      if (parts.length === 1 && parts[0] === 'conversation') {
        const conversationId = url.searchParams.get('cid')?.trim();
        if (conversationId) return { kind: 'support', conversationId };
      }
      break;
  }
  return { kind: 'invalid' };
}

export function parseActionablePushPayload(data: Record<string, unknown>): ActionablePushPayload | null {
  const type = nonEmpty(data.type);
  if (!type || type === 'cs_reply') return null;
  const category = nonEmpty(data.category);
  const deepLink = nonEmpty(data.deep_link);
  const primaryAction = nonEmpty(data.primary_action);
  if (!category || !deepLink || !primaryAction) return null;
  if (!isActionableCloudLink(deepLink) || resolveConciergeLink(deepLink).kind !== 'internal') return null;
  return {
    type,
    category,
    deepLink,
    primaryAction,
    actions: parseActionsJson(nonEmpty(data.actions_json)),
  };
}

export function actionablePushHref(payload: ActionablePushPayload, actionId?: string): string | null {
  if (actionId) {
    const link = payload.actions.find((a) => a.actionId === actionId)?.deepLink;
    if (!link) return null;
    const resolved = resolveConciergeLink(link);
    return resolved.kind === 'internal' ? resolved.href : null;
  }
  const resolved = resolveConciergeLink(payload.deepLink);
  return resolved.kind === 'internal' ? resolved.href : null;
}

export function navigateSpa(href: string): void {
  window.history.pushState({}, '', href);
  const event = typeof PopStateEvent === 'function' ? new PopStateEvent('popstate') : new Event('popstate');
  window.dispatchEvent(event);
}

function resolveLsCloudLink(url: URL): ConciergeLinkAction {
  const parts = url.pathname.split('/').filter(Boolean).map(safeDecode);
  switch (url.hostname) {
    case 'events': {
      const eventId = parts[0];
      if (!eventId) break;
      const deviceId = url.searchParams.get('device_id')?.trim();
      if (!deviceId) return { kind: 'internal', href: '/events' };
      const q = new URLSearchParams();
      q.set('event_id', eventId);
      const eventTs = url.searchParams.get('event_ts')?.trim();
      if (eventTs) q.set('event_ts', eventTs);
      return { kind: 'internal', href: `/events/${encodeURIComponent(deviceId)}?${q.toString()}` };
    }
    case 'devices': {
      const deviceId = parts[0];
      if (deviceId) return { kind: 'internal', href: `/devices/${encodeURIComponent(deviceId)}` };
      break;
    }
    case 'live': {
      const deviceId = parts[0];
      if (deviceId) return { kind: 'internal', href: `/devices/${encodeURIComponent(deviceId)}/live` };
      break;
    }
  }
  return { kind: 'invalid' };
}

function parseActionsJson(raw?: string): ActionablePushAction[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.flatMap((item) => {
      if (!isRecord(item)) return [];
      const actionId = nonEmpty(item.action_id);
      const labelKey = nonEmpty(item.label_key);
      if (!actionId || !labelKey) return [];
      return [{
        actionId,
        labelKey,
        deepLink: internalActionLink(nonEmpty(item.deep_link)),
        params: stringifyParams(item.params),
      }];
    });
  } catch {
    return [];
  }
}

function internalActionLink(raw?: string): string | undefined {
  if (!raw) return undefined;
  return isActionableCloudLink(raw) && resolveConciergeLink(raw).kind === 'internal' ? raw : undefined;
}

function isActionableCloudLink(raw: string): boolean {
  try {
    const base = typeof window === 'undefined' ? 'https://home.local' : window.location.origin;
    return new URL(raw, base).protocol === 'lscloud:';
  } catch {
    return false;
  }
}

function stringifyParams(raw: unknown): Record<string, string> {
  if (!isRecord(raw)) return {};
  return Object.fromEntries(
    Object.entries(raw)
      .filter(([, value]) => typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean')
      .map(([key, value]) => [key, String(value)]),
  );
}

function nonEmpty(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

function safeDecode(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}
